// API Service for Alignworx Platform
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5133';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem('alignworks_token');
  }

  setToken(token) {
    localStorage.setItem('alignworks_token', token);
  }

  getRefreshToken() {
    return localStorage.getItem('alignworks_refresh_token');
  }

  setRefreshToken(refreshToken) {
    localStorage.setItem('alignworks_refresh_token', refreshToken);
  }

  clearToken() {
    localStorage.removeItem('alignworks_token');
    localStorage.removeItem('alignworks_refresh_token');
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.error('No refresh token available');
      return false;
    }

    try {
      console.log('Attempting to refresh access token...');
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      console.log('Refresh token response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        this.setToken(data.token);
        this.setRefreshToken(data.refreshToken);
        console.log('Token refreshed successfully');
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Token refresh failed:', response.status, errorData);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    return false;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    console.log(`[API Request] ${options.method || 'GET'} ${url}`);

    // Don't set Content-Type for FormData - browser will set it with boundary
    const headers = {};
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Merge with any custom headers
    Object.assign(headers, options.headers || {});

    const token = this.getToken();
    console.log('[API] Token exists:', !!token);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('[API] Authorization header set');
    } else {
      console.log('[API] WARNING: No token found!');
    }

    try {
      console.log('[API] Sending request...');
      let response = await fetch(url, {
        ...options,
        headers,
      });
      console.log(`[API] Response status: ${response.status}`);

      // If 401 and not already a refresh request, try to refresh token
      if (response.status === 401 && !endpoint.includes('/api/auth/refresh')) {
        console.log('[API] 401 Unauthorized - Access token expired, attempting refresh...');
        console.log('Current endpoint:', endpoint);
        const refreshed = await this.refreshAccessToken();
        console.log('Refresh result:', refreshed);

        if (refreshed) {
          // Retry original request with new token
          console.log('Retrying request with new token...');
          const newToken = this.getToken();
          if (newToken) {
            headers['Authorization'] = `Bearer ${newToken}`;
          }
          response = await fetch(url, {
            ...options,
            headers,
          });
          console.log('Retry response status:', response.status);
        } else {
          // Refresh failed, redirect to login
          console.error('Token refresh failed - redirecting to login');
          this.clearToken();
          localStorage.removeItem('alignworks_user');
          window.location.href = '/login';
          throw new Error('Session expired, please login again');
        }
      }

      if (response.status === 401) {
        // Still 401 after refresh attempt, redirect to login
        this.clearToken();
        localStorage.removeItem('alignworks_user');
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      // Parse JSON response
      const contentType = response.headers.get("content-type");
      let data = {};

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        // Extract error message from various possible fields
        let errorMessage = 'API request failed';

        // Check for validation errors first (from .NET model validation)
        if (data.errors && typeof data.errors === 'object') {
          // Extract all validation error messages
          const validationErrors = [];
          for (const field in data.errors) {
            const fieldErrors = data.errors[field];
            if (Array.isArray(fieldErrors)) {
              validationErrors.push(...fieldErrors);
            }
          }
          if (validationErrors.length > 0) {
            errorMessage = validationErrors.join(', ');
          }
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.title) {
          errorMessage = data.title;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }

        console.error('API Error Response:', { status: response.status, data, errorMessage });
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    // Handle params option - convert to query string
    let url = endpoint;
    if (options.params) {
      const queryString = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          queryString.append(key, value);
        }
      });
      const queryStr = queryString.toString();
      if (queryStr) {
        url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryStr}`;
      }
      // Remove params from options so it doesn't get passed to fetch
      const { params, ...restOptions } = options;
      options = restOptions;
    }

    return this.request(url, {
      ...options,
      method: 'GET',
    });
  }

  async post(endpoint, data, options = {}) {
    console.log(`[API] POST ${endpoint}`, data);
    // Don't stringify FormData - send it as-is
    const body = data instanceof FormData ? data : JSON.stringify(data);

    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: body,
    });
  }

  async put(endpoint, data, options = {}) {
    // Don't stringify FormData - send it as-is
    const body = data instanceof FormData ? data : JSON.stringify(data);

    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: body,
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  // Auth endpoints
  async login(identifier, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
    // Store both JWT access token and refresh token from .NET backend
    this.setToken(data.token);
    this.setRefreshToken(data.refreshToken);
    // Transform response to match AuthContext expectations
    return {
      user: {
        id: data.userId,
        email: data.email,
        name: data.name,
        role: data.role
      },
      token: data.token
    };
  }

  async register(userData) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    this.setToken(data.access_token);
    return data;
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  async updateSelfProfile(data) {
    return this.request('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Users endpoints
  async getUsers(page = 1, pageSize = 10) {
    return this.request(`/api/users?page=${page}&pageSize=${pageSize}`);
  }

  async getUser(id) {
    return this.request(`/api/users/${id}`);
  }

  async createUser(userData) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id, userData) {
    return this.request(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // OEMs endpoints
  async getOems(page = 1, pageSize = 100) {
    return this.request(`/api/oems?page=${page}&pageSize=${pageSize}`);
  }

  // OEM self profile (current logged-in OEM user)
  async getMyOem() {
    return this.request('/api/oems/me');
  }

  async updateMyOem(data) {
    return this.request('/api/oems/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Dashboard endpoints
  async getDashboardKpis() {
    return this.request('/api/dashboard/kpis');
  }

  // Locations endpoints
  async getLocations(page = 1, pageSize = 10) {
    return this.request(`/api/locations?page=${page}&pageSize=${pageSize}`);
  }

  async getLocation(id) {
    return this.request(`/api/locations/${id}`);
  }

  async getStates() {
    return this.request('/api/locations/states');
  }

  async createLocation(locationData) {
    return this.request('/api/locations', {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  }

  async updateLocation(id, locationData) {
    return this.request(`/api/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData),
    });
  }

  // Cities endpoints
  async getCities() {
    return this.request('/api/cities');
  }

  // Pricing Types endpoints
  async getPricingTypes(page = 1, pageSize = 10) {
    return this.request(`/api/pricingtypes?pageNumber=${page}&pageSize=${pageSize}`);
  }

  async getPricingType(id) {
    return this.request(`/api/pricingtypes/${id}`);
  }

  async createPricingType(pricingTypeData) {
    return this.request('/api/pricingtypes', {
      method: 'POST',
      body: JSON.stringify(pricingTypeData),
    });
  }

  async updatePricingType(id, pricingTypeData) {
    return this.request(`/api/pricingtypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pricingTypeData),
    });
  }

  async deletePricingType(id) {
    return this.request(`/api/pricingtypes/${id}`, {
      method: 'DELETE',
    });
  }

  // SV1 Checklist endpoints
  async getSv1ChecklistItems(pageNumber = 1, pageSize = 10, searchTerm = '') {
    const params = new URLSearchParams({ pageNumber, pageSize });
    if (searchTerm) params.append('searchTerm', searchTerm);
    return this.request(`/api/sv1checklist?${params.toString()}`);
  }

  async getSv1ChecklistItem(id) {
    return this.request(`/api/sv1checklist/${id}`);
  }

  async createSv1ChecklistItem(itemData) {
    return this.request('/api/sv1checklist', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async updateSv1ChecklistItem(id, itemData) {
    return this.request(`/api/sv1checklist/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  }

  async deleteSv1ChecklistItem(id) {
    return this.request(`/api/sv1checklist/${id}`, {
      method: 'DELETE',
    });
  }

  // SV2 Checklist endpoints
  async getSv2ChecklistItems(pageNumber = 1, pageSize = 10, searchTerm = '') {
    const params = new URLSearchParams({ pageNumber, pageSize });
    if (searchTerm) params.append('searchTerm', searchTerm);
    return this.request(`/api/sv2checklist?${params.toString()}`);
  }

  async getSv2ChecklistItem(id) {
    return this.request(`/api/sv2checklist/${id}`);
  }

  async createSv2ChecklistItem(itemData) {
    return this.request('/api/sv2checklist', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async updateSv2ChecklistItem(id, itemData) {
    return this.request(`/api/sv2checklist/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  }

  async deleteSv2ChecklistItem(id) {
    return this.request(`/api/sv2checklist/${id}`, {
      method: 'DELETE',
    });
  }

  // Client Review Checklist endpoints
  async getClientReviewChecklistItems(pageNumber = 1, pageSize = 10, searchTerm = '') {
    const params = new URLSearchParams({ pageNumber, pageSize });
    if (searchTerm) params.append('searchTerm', searchTerm);
    return this.request(`/api/clientreviewchecklist?${params.toString()}`);
  }

  async getClientReviewChecklistItem(id) {
    return this.request(`/api/clientreviewchecklist/${id}`);
  }

  async createClientReviewChecklistItem(itemData) {
    return this.request('/api/clientreviewchecklist', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async updateClientReviewChecklistItem(id, itemData) {
    return this.request(`/api/clientreviewchecklist/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  }

  async deleteClientReviewChecklistItem(id) {
    return this.request(`/api/clientreviewchecklist/${id}`, {
      method: 'DELETE',
    });
  }

  // Holidays endpoints
  async getHolidays(year = null) {
    const params = new URLSearchParams({ pageSize: 100 });
    if (year) params.append('year', year);
    return this.request(`/api/holidays?${params.toString()}`);
  }

  async createHoliday(data) {
    return this.request('/api/holidays', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteHoliday(id) {
    return this.request(`/api/holidays/${id}`, {
      method: 'DELETE',
    });
  }

  // Bills endpoints
  async getProjectBills(projectId) {
    return this.request(`/api/projects/${projectId}/bills`);
  }

  async uploadProjectBill(projectId, formData) {
    const token = this.getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseUrl}/api/projects/${projectId}/bills/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload bill');
    }
    return response.json();
  }

  async updateProjectBill(projectId, billId, data) {
    return this.request(`/api/projects/${projectId}/bills/${billId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectBill(projectId, billId) {
    return this.request(`/api/projects/${projectId}/bills/${billId}`, {
      method: 'DELETE',
    });
  }

  async getBillDownloadUrl(projectId, billId) {
    return this.request(`/api/projects/${projectId}/bills/${billId}/download`);
  }

  getBillStreamUrl(projectId, billId) {
    return `${this.baseUrl}/api/projects/${projectId}/bills/${billId}/stream`;
  }

  // Projects endpoints
  async getProjects(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/api/projects${queryString ? `?${queryString}` : ''}`);
  }

  async getProject(id) {
    return this.request(`/api/projects/${id}`);
  }

  async createProject(projectData, files = []) {
    const formData = new FormData();

    // Add project data as JSON string
    formData.append('projectData', JSON.stringify(projectData));

    // Add files with sub-categories if any
    if (files && files.length > 0) {
      files.forEach((fileObj, index) => {
        // If it's the new format {file, subCategory}
        if (fileObj.file) {
          formData.append('files', fileObj.file);
          formData.append(`subCategories[${index}]`, fileObj.subCategory || '');
        } else {
          // Backward compatibility: if it's just a file object
          formData.append('files', fileObj);
          formData.append(`subCategories[${index}]`, '');
        }
      });
    }

    const url = `${this.baseUrl}/api/projects`;
    const token = this.getToken();

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type - browser will set it with boundary for multipart

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      // Backend returns different structures: {message} or {title} or {errors}
      const errorMessage = error.message || error.title || error.errors?.join(', ') || 'Failed to create project';
      throw new Error(errorMessage);
    }

    return { data: await response.json() };
  }

  async updateProject(id, updateData) {
    return this.request(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteProject(id) {
    return this.request(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async putProjectOnHold(projectId, reason) {
    return this.request(`/api/projects/${projectId}/hold`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async resumeProject(projectId, data = {}) {
    return this.request(`/api/projects/${projectId}/resume`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── Project Requests / Discrepancies ──

  async getProjectRequests(projectId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/projects/${projectId}/requests${query ? `?${query}` : ''}`);
  }

  async getProjectRequest(projectId, requestId) {
    return this.request(`/api/projects/${projectId}/requests/${requestId}`);
  }

  async createProjectRequest(projectId, data) {
    return this.request(`/api/projects/${projectId}/requests`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProjectRequest(projectId, requestId, data) {
    return this.request(`/api/projects/${projectId}/requests/${requestId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async closeProjectRequest(projectId, requestId, data = {}) {
    return this.request(`/api/projects/${projectId}/requests/${requestId}/close`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectRequest(projectId, requestId) {
    return this.request(`/api/projects/${projectId}/requests/${requestId}`, {
      method: 'DELETE',
    });
  }

  async assignTeamToProject(projectId, teamData) {
    return this.request(`/api/projects/${projectId}/assign-team`, {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  }

  async getProjectTeams(projectId) {
    return this.request(`/api/projects/${projectId}/teams`);
  }

  async addTeamToProject(projectId, teamId) {
    return this.request(`/api/projects/${projectId}/teams`, {
      method: 'POST',
      body: JSON.stringify({ teamId }),
    });
  }

  async removeTeamFromProject(projectId, teamId) {
    return this.request(`/api/projects/${projectId}/teams/${teamId}`, {
      method: 'DELETE',
    });
  }

  async scheduleInstallation(projectId, data) {
    return this.request(`/api/projects/${projectId}/schedule-installation`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadProjectFiles(projectId, formData) {
    // Get all files from formData
    const files = formData.getAll('files');
    const results = [];

    // Upload each file separately with document type
    for (const file of files) {
      const fileFormData = new FormData();
      fileFormData.append('file', file);
      fileFormData.append('documentTypeCode', 'project_document');
      fileFormData.append('subfolder', 'general');

      const url = `${this.baseUrl}/api/projects/${projectId}/documents/upload`;
      const token = this.getToken();

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: fileFormData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload files');
      }

      const result = await response.json();
      results.push(result);
    }

    return results;
  }

  // Teams endpoints
  async getTeams() {
    return this.request('/api/teams');
  }

  async getTeam(id) {
    return this.request(`/api/teams/${id}`);
  }

  async createTeam(teamData) {
    return this.request('/api/teams', {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  }

  async getAvailableUsersForTeams() {
    return this.request('/api/teams/available-users');
  }

  // Team Members endpoints
  async getTeamMembersForTeam(teamId) {
    return this.request(`/api/teams/${teamId}/members`);
  }

  async addTeamMember(teamId, userId) {
    return this.request(`/api/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async removeTeamMember(teamId, userId) {
    return this.request(`/api/teams/${teamId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateTeam(id, updateData) {
    return this.request(`/api/teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteTeam(id) {
    return this.request(`/api/teams/${id}`, {
      method: 'DELETE',
    });
  }

  // Team Members endpoints
  async getTeamMembers(team = null) {
    const queryString = team ? `?team=${encodeURIComponent(team)}` : '';
    return this.request(`/api/team-members${queryString}`);
  }

  async createTeamMember(memberData) {
    return this.request('/api/team-members', {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  }

  // Installer project endpoints
  async getInstallerProjects(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/api/installer/projects${queryString ? `?${queryString}` : ''}`);
  }

  async getInstallerProjectCounts() {
    return this.request('/api/installer/projects/counts');
  }

  async getInstallerProjectChecklist(projectId) {
    return this.request(`/api/installer/projects/${projectId}/checklist`);
  }

  async updateChecklistItem(projectId, responseId, isCompleted, notes = null) {
    return this.request(`/api/installer/projects/${projectId}/checklist/${responseId}`, {
      method: 'PUT',
      body: JSON.stringify({
        isCompleted,
        notes
      }),
    });
  }

  async batchUpdateChecklistItems(projectId, items) {
    return this.request(`/api/installer/projects/${projectId}/checklist/batch`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async markAttendance(projectId, latitude, longitude, accuracy, selfieFile) {
    const formData = new FormData();
    formData.append('ProjectId', projectId);
    formData.append('Latitude', latitude.toString());
    formData.append('Longitude', longitude.toString());
    if (accuracy) formData.append('Accuracy', accuracy.toString());
    formData.append('Selfie', selfieFile);

    const token = localStorage.getItem('alignworks_token');
    const response = await fetch(`${this.baseUrl}/api/installer/attendance`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to mark attendance');
    }
    return response.json();
  }

  async getMyAttendance(month, year) {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    const qs = params.toString();
    return this.request(`/api/installer/attendance${qs ? `?${qs}` : ''}`);
  }

  async checkOutAttendance(attendanceId, latitude, longitude, accuracy, selfieFile) {
    const formData = new FormData();
    formData.append('Latitude', latitude.toString());
    formData.append('Longitude', longitude.toString());
    if (accuracy) formData.append('Accuracy', accuracy.toString());
    formData.append('Selfie', selfieFile);

    const token = localStorage.getItem('alignworks_token');
    const response = await fetch(`${this.baseUrl}/api/installer/attendance/${attendanceId}/checkout`, {
      method: 'PUT',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to clock out');
    }
    return response.json();
  }

  async getProjectAttendance(projectId) {
    return this.request(`/api/projects/${projectId}/attendance`);
  }

  async completeInstallation(projectId) {
    return this.request(`/api/installer/projects/${projectId}/complete-installation`, {
      method: 'POST',
    });
  }

  // Client Review endpoints (Installer)
  async getInstallerClientReview(projectId) {
    return this.request(`/api/installer/projects/${projectId}/client-review`);
  }

  async updateClientReviewItem(projectId, responseId, isCompleted, notes = null) {
    return this.request(`/api/installer/projects/${projectId}/client-review/${responseId}`, {
      method: 'PUT',
      body: JSON.stringify({ isCompleted, notes }),
    });
  }

  // Batch-update client-review items in a single request.
  // items: [{ responseId, isCompleted, notes }]
  async batchUpdateClientReviewItems(projectId, items) {
    return this.request(`/api/installer/projects/${projectId}/client-review/batch`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async completeClientReview(projectId) {
    return this.request(`/api/installer/projects/${projectId}/complete-client-review`, {
      method: 'POST',
    });
  }

  // Progress Report endpoints (Installer)
  async submitProgressReport(data) {
    return this.request('/api/installer/progress-report', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyProgressReports(projectId = null) {
    const query = projectId ? `?projectId=${projectId}` : '';
    return this.request(`/api/installer/progress-reports${query}`);
  }

  // Progress Report endpoints (Admin)
  async getProjectProgressReports(projectId) {
    return this.request(`/api/projects/${projectId}/progress-reports`);
  }

  async toggleProgressOverride(projectId) {
    return this.request(`/api/projects/${projectId}/toggle-progress-override`, {
      method: 'PUT',
    });
  }

  async updateLocationRadius(projectId, radius) {
    return this.request(`/api/projects/${projectId}/location-radius`, {
      method: 'PUT',
      body: JSON.stringify({ radius }),
    });
  }

  // Scheduler endpoints
  async getMonthSchedule(year, month, role) {
    const params = new URLSearchParams({ year, month });
    if (role) params.append('role', role);
    return this.request(`/api/scheduler/month-schedule?${params}`);
  }

  async getDaySchedule(date, role) {
    const params = new URLSearchParams({ date });
    if (role) params.append('role', role);
    return this.request(`/api/scheduler/day-schedule?${params}`);
  }

  getSchedulerRosterUrl(year, month, role) {
    const params = new URLSearchParams({ year, month });
    if (role) params.append('role', role);
    return `${this.baseUrl}/api/scheduler/roster?${params}`;
  }

  // OEMs endpoints
  async getOEMs() {
    return this.request('/api/oems');
  }

  async getOEM(id) {
    return this.request(`/api/oems/${id}`);
  }

  // Pricing endpoints
  async getPricing() {
    return this.request('/api/pricing');
  }

  async updatePricing(id, updateData) {
    return this.request(`/api/pricing/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  // Dashboard endpoints
  async getKPIs() {
    return this.request('/api/dashboard/kpis');
  }

  async getRecentCompleted() {
    return this.request('/api/dashboard/recent-completed');
  }

  // Installer incentives
  async getInstallerIncentives() {
    return this.request('/api/installer/incentives');
  }

  // ==================== SUPERVISOR ENDPOINTS ====================

  // Get list of all supervisors
  async getSupervisors() {
    return this.request('/api/supervisors');
  }

  // Get projects assigned to supervisor
  async getSupervisorProjects() {
    return this.request('/api/supervisor/projects');
  }

  // Get supervisor monthly schedule (SV bookings + installations)
  async getSupervisorSchedule(year, month) {
    return this.request(`/api/supervisor/schedule?year=${year}&month=${month}`);
  }

  async getSvInProgress() {
    return this.request('/api/supervisor/site-visits/in-progress');
  }

  async getSvCompleted() {
    return this.request('/api/supervisor/site-visits/completed');
  }

  // Get all installers for supervisor to monitor
  async getSupervisorInstallers() {
    return this.request('/api/supervisor/installers');
  }

  // Supervisor creates new installer
  async createInstallerBySupervisor(installerData) {
    return this.request('/api/supervisor/installer', {
      method: 'POST',
      body: JSON.stringify(installerData),
    });
  }

  // Supervisor views all incentives
  async getSupervisorIncentivesView() {
    return this.request('/api/supervisor/incentives');
  }

  // Update site visit checklist
  // Start site visit (seeds checklist and sets InProgress)
  async startSiteVisit(projectId, visitNumber) {
    return this.request(`/api/supervisor/start-site-visit/${projectId}/${visitNumber}`, {
      method: 'POST',
    });
  }

  async updateSiteVisit(projectId, visitNumber, checklist, pendingItems = null, reworkDetails = null) {
    const requestBody = {
      checklist: checklist,
      pending_items: pendingItems,
      rework_details: reworkDetails
    };

    return this.request(`/api/supervisor/site-visit/${projectId}/${visitNumber}`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    });
  }

  // Complete site visit
  async completeSiteVisit(projectId, visitNumber) {
    return this.request(`/api/supervisor/complete-site-visit/${projectId}/${visitNumber}`, {
      method: 'POST',
    });
  }

  // Get installation checklist for supervisor
  async getSupervisorChecklist(projectId) {
    return this.request(`/api/supervisor/projects/${projectId}/installation-checklist`);
  }

  // Approve a checklist item (supervisor)
  async supervisorApproveChecklist(projectId, responseId) {
    return this.request(`/api/supervisor/projects/${projectId}/checklist/${responseId}/approve`, {
      method: 'PUT',
    });
  }

  // Client Review endpoints (Supervisor)
  async getSupervisorClientReview(projectId) {
    return this.request(`/api/supervisor/projects/${projectId}/client-review`);
  }

  async supervisorApproveClientReview(projectId, responseId) {
    return this.request(`/api/supervisor/projects/${projectId}/client-review/${responseId}/approve`, {
      method: 'PUT',
    });
  }

  async supervisorUpdateClientReview(projectId, responseId, isCompleted, notes = null) {
    return this.request(`/api/supervisor/projects/${projectId}/client-review/${responseId}/update`, {
      method: 'PUT',
      body: JSON.stringify({ isCompleted, notes }),
    });
  }

  async supervisorCompleteClientReview(projectId) {
    return this.request(`/api/supervisor/projects/${projectId}/complete-client-review`, {
      method: 'POST',
    });
  }

  // Get jobs progress for supervisor monitoring
  async getSupervisorJobsProgress() {
    return this.request('/api/supervisor/jobs-progress');
  }

  // Supervisor approve job completion
  async supervisorApproveCompletion(jobId, approved, notes = null) {
    const params = new URLSearchParams({ approved: approved.toString() });
    if (notes) params.append('notes', notes);
    return this.request(`/api/supervisor/approve-completion/${jobId}?${params}`, {
      method: 'POST',
    });
  }

  // ==================== ADMIN SUPERVISOR MANAGEMENT ====================

  // Admin assigns supervisor to project
  async assignSupervisor(projectId, supervisorId, siteVisit1Date) {
    const params = new URLSearchParams({
      supervisor_id: supervisorId,
      site_visit_1_date: siteVisit1Date
    });
    return this.request(`/api/admin/assign-supervisor/${projectId}?${params}`, {
      method: 'POST',
    });
  }

  // Admin proposes site visit 2 (date + slot + supervisor)
  async proposeSiteVisit2(projectId, date, slotId, supervisorId) {
    const params = new URLSearchParams({ date, slotId: slotId.toString(), supervisorId });
    return this.request(`/api/admin/propose-site-visit-2/${projectId}?${params}`, {
      method: 'POST',
    });
  }

  // OEM approves SV2 (approve as-is or reschedule)
  async approveSv2(projectId, { reschedule = false, newDate = null, newSlotId = null } = {}) {
    return this.request(`/api/projects/approve-sv2/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ reschedule, newDate, newSlotId }),
    });
  }

  // Admin updates SV1 booking (date, slot, supervisor)
  async updateSv1Booking(bookingId, date, slotId, supervisorId) {
    const params = new URLSearchParams({ date, slotId: slotId.toString(), supervisorId });
    return this.request(`/api/admin/update-sv1-booking/${bookingId}?${params}`, {
      method: 'PUT',
    });
  }

  // Admin updates SV2 booking (date, slot, supervisor)
  async updateSv2Booking(bookingId, date, slotId, supervisorId) {
    const params = new URLSearchParams({ date, slotId: slotId.toString(), supervisorId });
    return this.request(`/api/admin/update-sv2-booking/${bookingId}?${params}`, {
      method: 'PUT',
    });
  }

  // Admin updates project quote
  async updateProjectQuote(projectId, revisedQuote, newScheduledDate = null, reason = null) {
    const params = new URLSearchParams({ revised_quote: revisedQuote.toString() });
    if (newScheduledDate) params.append('new_scheduled_date', newScheduledDate);
    if (reason) params.append('schedule_change_reason', reason);
    return this.request(`/api/admin/update-quote/${projectId}?${params}`, {
      method: 'POST',
    });
  }

  // Get site visit requests
  async getSiteVisitRequests() {
    return this.request('/api/admin/site-visit-requests');
  }

  // Approve site visit request
  async approveSiteVisitRequest(requestId, approved, supervisorId = null) {
    const params = new URLSearchParams({ approved: approved.toString() });
    if (supervisorId) params.append('supervisor_id', supervisorId);
    return this.request(`/api/admin/approve-site-visit-request/${requestId}?${params}`, {
      method: 'POST',
    });
  }

  // ==================== OEM SITE VISIT REQUESTS ====================

  // OEM approves quote/schedule changes
  async oemApproveChanges(projectId, approved) {
    const params = new URLSearchParams({ approved: approved.toString() });
    return this.request(`/api/oem/approve-changes/${projectId}?${params}`, {
      method: 'POST',
    });
  }

  // OEM requests site visit
  async oemRequestSiteVisit(projectId, requestedDate, visitType, materialArrived = false) {
    const params = new URLSearchParams({
      requested_date: requestedDate,
      visit_type: visitType,
      material_arrived: materialArrived.toString()
    });
    return this.request(`/api/oem/request-site-visit/${projectId}?${params}`, {
      method: 'POST',
    });
  }

  // ==================== OEM SIGNOFF ====================

  // Get signoff details for a project (OEM view)
  async getOemSignoffDetails(projectId) {
    return this.request(`/api/oem-signoff/projects/${projectId}`);
  }

  // OEM approves signoff with rating and notes
  async oemApproveSignoff(projectId, rating, notes) {
    return this.request(`/api/oem-signoff/projects/${projectId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ rating, notes }),
    });
  }

  // ==================== ADMIN SIGNOFF / CLOSURE ====================

  // Get signoff details for admin review
  async getAdminSignoffDetails(projectId) {
    return this.request(`/api/admin/projects/${projectId}/signoff`);
  }

  // Admin closes the project
  async adminCloseProject(projectId, notes) {
    return this.request(`/api/admin/projects/${projectId}/close`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Seed data (for development)
  async seedData() {
    return this.request('/api/seed', {
      method: 'POST',
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/api/health');
  }

  // ==================== ENTITY MASTER ENDPOINTS ====================

  // Entity endpoints
  async getEntities(pageNumber = 1, pageSize = 100, searchTerm = '') {
    const params = new URLSearchParams({ pageNumber, pageSize });
    if (searchTerm) params.append('searchTerm', searchTerm);
    return this.request(`/api/mstentity?${params.toString()}`);
  }

  async getEntity(id) {
    return this.request(`/api/mstentity/${id}`);
  }

  async createEntity(entityData) {
    return this.request('/api/mstentity', {
      method: 'POST',
      body: JSON.stringify(entityData),
    });
  }

  async createEntityWithChecklist(entityData) {
    return this.request('/api/mstentity/with-checklist', {
      method: 'POST',
      body: JSON.stringify(entityData),
    });
  }

  async updateEntity(id, entityData) {
    return this.request(`/api/mstentity/${id}`, {
      method: 'PUT',
      body: JSON.stringify(entityData),
    });
  }

  async deleteEntity(id) {
    return this.request(`/api/mstentity/${id}`, {
      method: 'DELETE',
    });
  }

  // Entity Checklist endpoints
  async getEntityChecklistItems(entityId) {
    return this.request(`/api/mstentity/${entityId}/checklist`);
  }

  async getChecklistItem(id) {
    return this.request(`/api/mstentity/checklist/${id}`);
  }

  async createChecklistItem(checklistData) {
    return this.request('/api/mstentity/checklist', {
      method: 'POST',
      body: JSON.stringify(checklistData),
    });
  }

  async updateMstChecklistItem(id, checklistData) {
    return this.request(`/api/mstentity/checklist/${id}`, {
      method: 'PUT',
      body: JSON.stringify(checklistData),
    });
  }

  async deleteChecklistItem(id) {
    return this.request(`/api/mstentity/checklist/${id}`, {
      method: 'DELETE',
    });
  }

  // Configuration endpoints
  async getAllEntitiesWithChecklists() {
    return this.request('/api/mstentity/all-with-checklists', {
      method: 'GET',
    });
  }

  async bulkUpdateConfiguration(configData) {
    return this.request('/api/mstentity/bulk-update-configuration', {
      method: 'PUT',
      body: JSON.stringify(configData),
    });
  }

  // ==================== SUPERVISOR SLOT ENDPOINTS ====================

  // Get slot availability for a specific date
  async getSlotAvailability(date, excludeProjectId = null) {
    // Format date in local timezone to avoid timezone shift issues
    const dateStr = date instanceof Date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : date;
    let url = `/api/supervisor-slots/availability?date=${dateStr}`;
    if (excludeProjectId) url += `&excludeProjectId=${excludeProjectId}`;
    return this.request(url);
  }

  // Get available supervisors for a specific date and slot
  async getAvailableSupervisors(date, slotId, excludeProjectId = null) {
    // Format date in local timezone to avoid timezone shift issues
    const dateStr = date instanceof Date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : date;
    let url = `/api/supervisor-slots/available-supervisors?date=${dateStr}&slotId=${slotId}`;
    if (excludeProjectId) url += `&excludeProjectId=${excludeProjectId}`;
    return this.request(url);
  }

  // Get month availability for calendar display
  async getMonthAvailability(year, month) {
    return this.request(`/api/supervisor-slots/month-availability?year=${year}&month=${month}`);
  }

  // Create and share quotation with OEM
  async createQuotation(quotationData) {
    return this.post('/api/projects/quotations', quotationData);
  }

  // Update existing quotation
  async updateQuotation(quotationId, quotationData) {
    return this.put(`/api/projects/quotations/${quotationId}`, {
      ...quotationData,
      quotationId: quotationId
    });
  }

  // ─── Notifications ────────────────────────────────────────
  async getNotifications(page = 1, pageSize = 20, unreadOnly = false) {
    return this.get(`/api/notifications?page=${page}&pageSize=${pageSize}&unreadOnly=${unreadOnly}`);
  }

  async getUnreadCount() {
    return this.get('/api/notifications/unread-count');
  }

  async markNotificationRead(id) {
    return this.put(`/api/notifications/${id}/read`);
  }

  async markAllNotificationsRead() {
    return this.put('/api/notifications/read-all');
  }

  async getProjectNotifications(projectId) {
    return this.get(`/api/notifications/project/${projectId}`);
  }

  async resendNotification(deliveryId) {
    return this.post(`/api/notifications/${deliveryId}/resend`);
  }

  // ─── Notification Templates (Admin) ──────────────────────
  async getNotificationTemplates(channel = 'email') {
    return this.get(`/api/notification-templates?channel=${channel}`);
  }

  async getNotificationTemplate(id) {
    return this.get(`/api/notification-templates/${id}`);
  }

  async updateNotificationTemplate(id, data) {
    return this.put(`/api/notification-templates/${id}`, data);
  }
}

const apiService = new ApiService();
export default apiService;
